import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { BehaviorSubject, catchError, Observable, throwError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private apiUrl = environment.backendApiUrl;

  public roleSubject: any = new BehaviorSubject('');
  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private router: Router
  ) {}

  private getSessionHeader(): { headers: HttpHeaders } {
    const sessionId = sessionStorage.getItem('Session_Id');
    return {
      headers: new HttpHeaders({
        'X-Session-ID': `${sessionId || ''}`,
      }),
    };
  }

  getAtlanHeath(selectedNode: string, selectedEnv: string): Observable<any[]> {
    const url = `${this.apiUrl}/atlan-metadata/health`;
    let params = new HttpParams();
    params = params.set('node_name', selectedNode);
    params = params.set('env_name', selectedEnv);
    const sessionId = sessionStorage.getItem('Session_Id') || '';
    return this.http
      .get<any[]>(url, {
        params,
        headers: { 'X-Session-ID': sessionId, Accept: 'application/json' },
      })
      .pipe(catchError(this.handleApiError.bind(this)));
  }

  getSnowflakeHeath(): Observable<any[]> {
    const sessionId = this.getSessionHeader();
    return this.http
      .get<any[]>(this.apiUrl + '/snowflake-metadata/health', sessionId)
      .pipe(catchError(this.handleApiError.bind(this)));
  }

  uploadFile(
    file: File,
    extraFields?: Record<string, string>
  ): Observable<HttpResponse<Blob>> {
    const sessionId = sessionStorage.getItem('Session_Id') || '';

    const form = new FormData();
    form.append('file', file, file.name);
    if (extraFields) {
      for (const [key, value] of Object.entries(extraFields)) {
        form.append(key, value);
      }
    }

    const headers = new HttpHeaders({
      'X-Session-ID': sessionId, // or 'Authorization': `Bearer ${sessionId}` if your backend expects that
    });

    return this.http
      .post<Blob>(`${this.apiUrl}/upload/upload-file`, form, {
        headers,
        responseType: 'blob' as 'json',
        observe: 'response',
      })
      .pipe(catchError(this.handleApiError.bind(this)));
  }

  getSavedList(): Observable<any[]> {
    const url = `${this.apiUrl}/generate-classification/save/list`;
    const params = new HttpParams().set('sort', 'newest');

    const sessionId = sessionStorage.getItem('Session_Id') || '';
    return this.http
      .get<any[]>(url, {
        params,
        headers: { 'X-Session-ID': sessionId, Accept: 'application/json' },
      })
      .pipe(catchError(this.handleApiError.bind(this)));
  }

  getFileClassificationDetails(filename: string): Observable<any[]> {
    const sessionId = this.getSessionHeader();
    return this.http
      .get<any[]>(
        this.apiUrl + '/generate-classification/save/' + filename,
        sessionId
      )
      .pipe(catchError(this.handleApiError.bind(this)));
  }

  downloadFileClassificationDetail(filename: string): Observable<any> {
    const sessionId = sessionStorage.getItem('Session_Id');
    const url =
      this.apiUrl + '/generate-classification/save/' + filename + '?format=csv';

    const headers = new HttpHeaders({
      'X-Session-ID': `${sessionId || ''}`,
    });

    return this.http
      .get(url, {
        headers,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(catchError(this.handleApiError.bind(this)));
  }

  private handleApiError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unexpected error occurred';

    if (error.status === 0) {
      errorMessage = 'Network error: Please check your internet connection';
    } else if (error.status === 401) {
      errorMessage = 'Session expired. Please login again.';
      setTimeout(() => this.router.navigate(['/login']), 2000);
    } else if (error.status === 403) {
      errorMessage = 'You do not have permission for this action';
    } else if (error.status === 404) {
      errorMessage = 'Content not found';
    } else if (error.status === 500) {
      errorMessage = 'Server Busy: Please try again';
    } else if (error.status >= 500) {
      errorMessage = 'Server error: Please try again later';
    }else if (error.status === 422) {
      errorMessage = 'File processing failed';
    }

    this.toastr.error(errorMessage, 'Error');

    return throwError(() => error);
  }
}
